import React, { useEffect } from 'react';
import parse from 'autosuggest-highlight/parse';
import throttle from 'lodash/throttle';
import PropTypes from 'prop-types';
import { useSnackbar } from 'notistack';

import {
  TextField,
  Grid,
  Typography,
  Box,
  IconButton
} from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import LocationOnIcon from '@material-ui/icons/LocationOn';
import CloseRoundedIcon from '@material-ui/icons/CloseRounded';
import { makeStyles } from '@material-ui/core/styles';

function loadScript(src, position, id) {
  if (!position) {
    return;
  }

  const script = document.createElement('script');
  script.setAttribute('async', '');
  script.setAttribute('id', id);
  script.src = src;
  position.appendChild(script);
}

const autocompleteService = { current: null };
const geocodeService = { current: null };

const useStyles = makeStyles((theme) => ({
  icon: {
    color: theme.palette.text.secondary,
    marginRight: theme.spacing(2)
  },
  search: {
    // position: 'absolute',
    // left: 0, // theme.spacing(4) + 30,
    // right: 0,
    // top: theme.spacing(2), // theme.spacing(2),
    // marginRight: 'auto',
    // marginLeft: 'auto',
    // zIndex: 10
    position: 'absolute',
    left: theme.spacing(2),
    top: theme.spacing(2),
    zIndex: 10,
    width: 250
  },
  searchBox: {
    backgroundColor: theme.palette.background.default,
    borderRadius: theme.shape.borderRadius,
    boxShadow: theme.shadows[4]
  }
}));

// const formatAddress = (addressComponents) => {
//   let address = '';
//   const types = [
//     'route',
//     'administrative_area_level_3',
//     'administrative_area_level_1',
//     'postal_code'
//   ];

//   for (let comp of addressComponents) {
//     console.log('comp', comp);
//     if (comp.types.some((val) => types.indexOf(val) !== -1)) {
//       if (comp.types.includes(['administrative_area_level_1'])) address += ', ';
//       address += ` ${comp.short_name}`;
//     }
//   }

//   return address.trim();
// };

export default function Search(props) {
  const classes = useStyles();
  const [value, setValue] = React.useState(null);
  const [inputValue, setInputValue] = React.useState('');
  const [options, setOptions] = React.useState([]);
  const loaded = React.useRef(false);
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  const snackAction = (key) => (
    <IconButton onClick={() => closeSnackbar(key)}>
      <CloseRoundedIcon />
    </IconButton>
  );

  const snackbarOptions = {
    variant: 'error',
    anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
    autoHideDuration: 5000,
    action: snackAction
  };

  if (typeof window !== 'undefined' && !loaded.current) {
    if (!document.querySelector('#google-maps')) {
      loadScript(
        `https://maps.googleapis.com/maps/api/js?key=${process.env.REACT_APP_GOOGLE_KEY}&libraries=places`,
        document.querySelector('head'),
        'google-maps'
      );
    }

    loaded.current = true;
  }

  const fetch = React.useMemo(
    () =>
      throttle((request, callback) => {
        autocompleteService.current.getPlacePredictions(request, callback);
      }, 200),
    []
  );

  // autocomplete returns:
  // data = {
  //   description: '2012 McPherson lane nashville, tn usa',
  //   matched_strings: [{length: 4, offset: 0 }, {length: 6, offset: 5 }],
  //   place_id: 'string',
  //   reference: 'string',
  //   structured_formatting: { main_text: 'string', ... , secondary_text: 'string' },
  //   terms: [{ offest: 0, value: 2012 }, { offset: 5, value: 'McPherson Lane' }, ... ],
  //   types: ['street_address', 'geocode']
  // }

  useEffect(() => {
    let active = true;

    if (!autocompleteService.current && window.google) {
      autocompleteService.current = new window.google.maps.places.AutocompleteService();
    }
    if (!autocompleteService.current) {
      return undefined;
    }

    if (inputValue === '') {
      setOptions(value ? [value] : []);
      return undefined;
    }

    fetch({ input: inputValue }, (results) => {
      if (active) {
        let newOptions = [];

        if (value) {
          newOptions = [value];
        }

        if (results) {
          newOptions = [...newOptions, ...results];
        }

        setOptions(newOptions);
      }
    });

    return () => {
      active = false;
    };
  }, [value, inputValue, fetch]);

  const handleSelected = (event, newValue, reason) => {
    // console.log('new on change value - search option selected: ', newValue);
    setOptions(newValue ? [newValue, ...options] : options);
    setValue(newValue);

    if (reason !== 'select-option') return;

    if (!geocodeService.current && window.google && newValue.place_id) {
      geocodeService.current = new window.google.maps.Geocoder();
    }
    if (!geocodeService.current) {
      return undefined;
    }

    geocodeService.current.geocode(
      { placeId: newValue.place_id },
      (response, geocoderStatus) => {
        // console.log('google geocoder response', response);
        if (geocoderStatus === 'OK') {
          // let address = formatAddress(response[0].address_components);
          props.getPathsFromSearch(
            {
              lng: response[0].geometry.location.lng(),
              lat: response[0].geometry.location.lat()
            },
            newValue.structured_formatting.main_text || newValue.description
          );
        } else {
          enqueueSnackbar(
            "An Error occured while fetching the location's coordinates",
            snackbarOptions
          );
        }
      }
    );
  };

  return (
    <Autocomplete
      id='google-map-demo'
      style={{ width: 250 }}
      className={classes.search}
      size='small'
      getOptionLabel={(option) =>
        typeof option === 'string' ? option : option.description
      }
      filterOptions={(x) => x}
      options={options}
      autoHighlight={true}
      autoComplete={true}
      blurOnSelect={true} // 'touch'
      includeInputInList
      filterSelectedOptions
      value={value}
      onChange={handleSelected}
      onInputChange={(event, newInputValue, reason) => {
        setInputValue(newInputValue);
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label='Search'
          variant='outlined'
          fullWidth
          margin='none'
          color='primary'
          className={classes.searchBox}
        />
      )}
      renderOption={(option) => {
        const matches =
          option.structured_formatting.main_text_matched_substrings;
        const parts = parse(
          option.structured_formatting.main_text,
          matches.map((match) => [match.offset, match.offset + match.length])
        );

        return (
          <Grid container alignItems='center' wrap='nowrap'>
            <Grid item>
              <LocationOnIcon className={classes.icon} />
            </Grid>
            <Grid item xs zeroMinWidth>
              <Box
                component='div'
                overflow='hidden'
                textOverflow='ellipsis'
                whiteSpace='nowrap'
                fontSize='body1.fontSize'
              >
                {parts.map((part, index) => (
                  <span
                    key={index}
                    style={{ fontWeight: part.highlight ? 700 : 400 }}
                  >
                    {part.text}
                  </span>
                ))}
              </Box>

              <Typography variant='body2' color='textSecondary' noWrap>
                {option.structured_formatting.secondary_text}
              </Typography>
            </Grid>
          </Grid>
        );
      }}
    />
  );
}

Search.propTypes = {
  getPathsFromSearch: PropTypes.func.isRequired
};
